// src/pages/DailyAttendance.jsx - UPDATED with role check
import React, { useState, useRef, useEffect } from "react";
import MasterLayout from "../otherImages/MasterLayout";
import DefaultTopBar from "../components/DefaultTopBar";
import { useDispatch, useSelector } from "react-redux";
import {
  addDailyAttendance,
  downloadMyAttendanceCSV,
  getMyDailyAttendance,
  clearDailyAttendanceState,
} from "../redux/slices/dailyAttendanceSlice";
import Toast from "../components/Toast";
import DailyTeamAttendance from "../components/DailyTeamAttendance";
import DynamicModal from "../components/DynamicModal";
import AddDailyAttendanceModal from "../components/AddDailyAttendanceModal";

const DailyAttendance = () => {
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const teamAttendanceRef = useRef();

  const {
    isDownloadingMyCSV,
    downloadMyCSVError,
    isAdding,
    addError,
    successMessage,
  } = useSelector((state) => state.dailyAttendance);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });
  const toastTimeoutRef = useRef(null);

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
  }, []);

  const showToast = (message, type = "info") => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
      toastTimeoutRef.current = null;
    }, 3000);
  };

  const hideToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast({ show: false, message: "", type: "info" });
  };

  // Redux error handling
  useEffect(() => {
    if (downloadMyCSVError) {
      showToast(downloadMyCSVError, "error");
      dispatch(clearDailyAttendanceState());
    }
  }, [downloadMyCSVError, dispatch]);

  useEffect(() => {
    if (addError) {
      showToast(addError, "error");
      dispatch(clearDailyAttendanceState());
    }
  }, [addError, dispatch]);

  useEffect(() => {
    if (successMessage) {
      showToast(successMessage, "success");
      dispatch(clearDailyAttendanceState());
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const handleDownloadCSV = () => {
    // Get the local time difference in hours (offset from UTC)
    const localTimeDifference = -new Date().getTimezoneOffset() / 60;

    if (teamAttendanceRef.current) {
      const filterParams = teamAttendanceRef.current.getFilterParams();
      const paramsWithTimeZone = {
        ...filterParams,
        local_time_difference: localTimeDifference,
      };
      dispatch(downloadMyAttendanceCSV(paramsWithTimeZone)).then((result) => {
        if (result.error) {
          showToast(result.payload || "Failed to download CSV", "error");
        }
      });
    } else {
      dispatch(
        downloadMyAttendanceCSV({
          local_time_difference: localTimeDifference,
        }),
      ).then((result) => {
        if (result.error) {
          showToast(result.payload || "Failed to download CSV", "error");
        }
      });
    }
  };

  const handleAddAttendance = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleSaveAttendance = (attendanceData) => {
    // Send raw datetime strings to thunk for UTC conversion
    const apiData = {
      start_time: attendanceData.startDateTime,
      end_time: attendanceData.endDateTime,
      facility_id: parseInt(attendanceData.facility_id, 10),
    };

    dispatch(addDailyAttendance(apiData)).then((result) => {
      if (result.payload?.success) {
        showToast("Daily attendance added successfully!", "success");
        handleCloseAddModal();
        if (teamAttendanceRef.current) {
          const filterParams = teamAttendanceRef.current.getFilterParams();
          dispatch(getMyDailyAttendance(filterParams));
        } else {
          dispatch(getMyDailyAttendance());
        }
      } else {
        showToast(
          result.payload?.message || "Failed to add daily attendance",
          "error",
        );
      }
    });
  };

  const isTeam = userRole === "team";

  return (
    <>
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={3000}
        />
      )}

      <MasterLayout>
        <DefaultTopBar
          title="Facility Attendance"
          // Only show "Add Attendance" button if user role is "team"
          btnText={isTeam ? "Add Attendance" : undefined}
          btnLink="#"
          isApiButton={isTeam}
          onBtnClick={isTeam ? handleAddAttendance : undefined}
          isBtnLoading={isTeam ? isAdding : false}
          btnLoadingText={isTeam ? "Adding..." : undefined}
          btnText2="Download CSV"
          btnLink2="#"
          isApiButton2={true}
          onBtn2Click={handleDownloadCSV}
          isBtn2Loading={isDownloadingMyCSV}
          btn2LoadingText="Downloading..."
        />

        <DailyTeamAttendance ref={teamAttendanceRef} />

        {isTeam && (
          <DynamicModal
            show={showAddModal}
            handleClose={handleCloseAddModal}
            title="Facility Attendance"
            modalWidth="500px"
            content={
              <AddDailyAttendanceModal
                onClose={handleCloseAddModal}
                onSave={handleSaveAttendance}
                isSaving={isAdding}
              />
            }
          />
        )}
      </MasterLayout>
    </>
  );
};

export default DailyAttendance;
